import { Link } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { type HNItem } from "@/lib/hn";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { getSavedPosts, savePost, unsavePost } from "@/lib/storage";

interface PostCardProps {
  post: HNItem;
  rank: number;
}

export function PostCard({ post, rank }: PostCardProps) {
  const queryClient = useQueryClient();

  // Extract domain from URL
  let domain = "";
  try {
    domain = post.url ? new URL(post.url).hostname.replace("www.", "") : "";
  } catch {
    console.error("Invalid URL:", post.url);
  }

  // Make the component self-aware of the saved state across the application
  const { data: savedPosts } = useQuery<HNItem[]>({
    queryKey: ["savedPosts"],
    queryFn: async () => {
      return getSavedPosts();
    },
    staleTime: Infinity, // Rely on mutations to invalidate
  });

  // Calculate effective saved state
  const isEffectivelySaved = !!savedPosts?.find((sp: HNItem) => sp.id === post.id);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (isEffectivelySaved) {
        unsavePost(post.id);
      } else {
        savePost(post);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["savedPosts"] });
    },
  });

  // Alternate visual styles slightly for rank #1 or highlighted items
  const isTopItem = rank === 1 || rank === 2;

  if (isTopItem) {
    return (
      <article className="group flex flex-col sm:flex-row items-start sm:items-center gap-6 p-6 border-2 border-secondary bg-secondary text-on-secondary mb-2">
        <div className="flex flex-row sm:flex-col items-center gap-2 min-w-[40px]">
          <div className="text-on-secondary/60">
            <span className="material-symbols-outlined text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>arrow_drop_up</span>
          </div>
          <span className="text-sm font-black font-label">{post.score || 0}</span>
        </div>

        <div className="flex-1 relative z-10 w-full">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <span className="px-2 py-0.5 bg-background text-white text-[9px] font-black uppercase tracking-widest">TOP STORY</span>
            <span className="text-[10px] font-black uppercase tracking-widest opacity-70">
              {domain ? `${domain} • ` : ''}{formatDistanceToNow(post.time * 1000, { addSuffix: true })}
            </span>
          </div>

          {post.url ? (
            <a href={post.url} target="_blank" rel="noopener noreferrer" className="block text-2xl font-headline font-black uppercase italic leading-none mb-3 hover:underline">
              {post.title}
            </a>
          ) : (
            <Link to={`/hn/post/${post.id}`} className="block text-2xl font-headline font-black uppercase italic leading-none mb-3 hover:underline">
              {post.title}
            </Link>
          )}

          <div className="flex items-center gap-6 mt-3 flex-wrap">
            <Link to={`/hn/post/${post.id}`} className="flex items-center gap-1.5 text-[10px] font-black hover:underline uppercase tracking-widest">
              <span className="material-symbols-outlined text-base">chat_bubble</span>
              {post.descendants || 0} Comments
            </Link>
            <button
              onClick={(e) => { e.preventDefault(); saveMutation.mutate(); }}
              disabled={saveMutation.isPending}
              className={`text-[10px] font-black hover:underline uppercase tracking-widest flex items-center gap-1 ${isEffectivelySaved ? 'opacity-100' : 'opacity-70'}`}
            >
              <span className="material-symbols-outlined text-base" style={{ fontVariationSettings: isEffectivelySaved ? "'FILL' 1" : "'FILL' 0" }}>bookmark</span>
              {isEffectivelySaved ? 'Saved' : 'Bookmark'}
            </button>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex flex-col sm:flex-row items-start sm:items-center gap-6 p-5 border border-white/10 bg-surface-container-low/20 hover:bg-surface-container-low transition-all yellow-accent-border mb-2">
      <div className="flex flex-row sm:flex-col items-center gap-2 min-w-[40px]">
        <div className="text-on-surface-variant">
          <span className="material-symbols-outlined text-3xl" style={{ fontVariationSettings: "'FILL' 1" }}>arrow_drop_up</span>
        </div>
        <span className="text-sm font-black text-white font-label">{post.score || 0}</span>
      </div>

      <div className="flex-1 w-full">
        <div className="flex items-center gap-3 mb-2 flex-wrap">
          <span className="text-[9px] font-black text-secondary uppercase tracking-widest border border-secondary px-2 py-0.5 bg-transparent group-hover:bg-secondary group-hover:text-on-secondary transition-colors">
            {domain || "HN.COM"}
          </span>
          <span className="text-[10px] font-bold text-on-surface-variant uppercase">
            {formatDistanceToNow(post.time * 1000, { addSuffix: true })} • by {post.by}
          </span>
        </div>

        {post.url ? (
          <a href={post.url} target="_blank" rel="noopener noreferrer" className="block text-xl font-headline font-bold text-white group-hover:text-secondary transition-colors leading-snug mb-3">
            {post.title}
          </a>
        ) : (
          <Link to={`/hn/post/${post.id}`} className="block text-xl font-headline font-bold text-white group-hover:text-secondary transition-colors leading-snug mb-3">
            {post.title}
          </Link>
        )}

        <div className="flex items-center gap-6 mt-3 flex-wrap">
          <Link to={`/hn/post/${post.id}`} className="flex items-center gap-1.5 text-[10px] font-black text-on-surface-variant hover:text-secondary uppercase tracking-widest transition-colors">
            <span className="material-symbols-outlined text-base">chat_bubble</span>
            {post.descendants || 0} Comments
          </Link>
          <button
            onClick={(e) => { e.preventDefault(); saveMutation.mutate(); }}
            disabled={saveMutation.isPending}
            className={`flex items-center gap-1.5 text-[10px] font-black text-on-surface-variant hover:text-secondary uppercase tracking-widest transition-colors ${isEffectivelySaved ? 'text-secondary' : ''}`}
          >
            <span className="material-symbols-outlined text-base" style={{ fontVariationSettings: isEffectivelySaved ? "'FILL' 1" : "'FILL' 0" }}>bookmark</span>
            {isEffectivelySaved ? 'Saved' : 'Bookmark'}
          </button>
        </div>
      </div>
    </article>
  );
}
